import { createContext, useContext } from "react";

import chroma from "chroma-js";

import { BorderRadius } from "../../tailwindTheme";
import type { VenueBrand, BrandStyle } from "./schema";

interface VenueCssVariables {
  [index: `--venue-${string}`]: string;
}

export interface Brand extends VenueBrand {
  backgroundColor: chroma.Color;
  foregroundColor: chroma.Color;
  brandContrastColor: "white" | "black";
  isBrutalist: boolean;
  isSimple: boolean;
  isElegant: boolean;
  isPlayful: boolean;
  cssVariables: VenueCssVariables;
}
const findContrastColor = (brandColor: string) => {
  const contrastWith = (color: string) => chroma.contrast(brandColor, color);
  return contrastWith("white") > contrastWith("black") ? "white" : "black";
};

type CssVariableStyleOptions = {
  [index in BrandStyle]: string | undefined;
};
const radiuses: CssVariableStyleOptions = {
  brutalist: BorderRadius.none,
  simple: BorderRadius.md,
  elegant: BorderRadius.none,
  playful: BorderRadius.xl,
};

export const brandForContext = (venueBrand: VenueBrand): Brand => {
  const shadowsLg: CssVariableStyleOptions = {
    brutalist: `8px 8px 0px 0 ${venueBrand.brandColor}`,
    simple: undefined,
    elegant: undefined,
    playful: undefined,
  };
  const shadows: CssVariableStyleOptions = {
    brutalist: `4px 4px 0px 0 ${venueBrand.brandColor}`,
    simple: undefined,
    elegant: undefined,
    playful: undefined,
  };
  const bordersLg: CssVariableStyleOptions = {
    brutalist: `0`,
    simple: "4px",
    elegant: "2px",
    playful: "4px",
  };
  const colorScale = chroma
    .bezier([venueBrand.brandColor || "white", "white"])
    .scale()
    .correctLightness();
  const brandContrastColor = findContrastColor(
    venueBrand.brandColor || "white",
  );
  const foregroundColor = colorScale(0.95);
  const backgroundColor = colorScale(0.98);
  return {
    backgroundColor,
    foregroundColor,
    ...venueBrand,
    brandContrastColor,
    isBrutalist: venueBrand.brandStyle == "brutalist",
    isSimple: venueBrand.brandStyle == "simple" || !venueBrand.brandStyle,
    isElegant: venueBrand.brandStyle == "elegant",
    isPlayful: venueBrand.brandStyle == "playful",
    cssVariables: {
      "--venue-primary": venueBrand.brandColor,
      "--venue-secondary": venueBrand.secondaryColor,
      "--venue-tertiary": venueBrand.tertiaryColor,
      "--venue-highlight": venueBrand.highlightColor,
      "--venue-border-radius": radiuses[venueBrand.brandStyle] || "0",
      "--venue-box-shadow": shadows[venueBrand.brandStyle] || "none",
      "--venue-box-shadow-lg": shadowsLg[venueBrand.brandStyle] || "none",
      "--venue-border-lg": bordersLg[venueBrand.brandStyle] || "none",
    },
  };
};

export const BrandContext = createContext<Brand | null>(null);
export const useBrand = () => {
  const brand = useContext(BrandContext);
  if (!brand) {
    throw new Error("useBrand must be used within a BrandContext");
  }
  return brand;
};
